import { AMPLIFY_APP_PACKAGE, ATHLYN_APP_PACKAGE, normalizePackage } from "@/components/corporate/organizations/AppPackageFields";

/**
 * One list per choice for the create wizard and the company page (B11): countries are stored as
 * ISO codes ("AE"), languages are English and Arabic only (OP-D11). A value stored before (a
 * country written as its full name, another language) still shows as stored.
 */

export interface Option {
  value: string;
  label: string;
}

/**
 * The edit dialog's ISO-code list, plus the Gulf countries the create wizard offered (so no
 * country the wizard offered disappears).
 */
export const COUNTRY_OPTIONS: readonly Option[] = [
  { value: "AE", label: "United Arab Emirates" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "KW", label: "Kuwait" },
  { value: "QA", label: "Qatar" },
  { value: "OM", label: "Oman" },
  { value: "BH", label: "Bahrain" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "GB", label: "United Kingdom" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "NL", label: "Netherlands" },
  { value: "AU", label: "Australia" },
  { value: "SG", label: "Singapore" },
];

/** The country new companies start with. */
export const DEFAULT_COUNTRY = "AE";

/** The languages offered (OP-D11). */
export const LANGUAGE_OPTIONS: readonly Option[] = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
];

/** Names of the languages offered before OP-D11, to show a stored one by name. */
const FORMER_LANGUAGE_NAMES: Record<string, string> = {
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  nl: "Dutch",
};

export const NO_LONGER_OFFERED = "(no longer offered)";

/** The country options, with a stored value that is not in the list shown as stored (first). */
export function countryOptionsFor(stored: string | null | undefined): Option[] {
  const value = (stored ?? "").trim();
  if (!value || COUNTRY_OPTIONS.some((o) => o.value === value)) return [...COUNTRY_OPTIONS];
  return [{ value, label: value }, ...COUNTRY_OPTIONS];
}

/** A country's label: the list's name for a code, else the stored value itself. */
export function countryLabel(stored: string | null | undefined): string {
  const value = (stored ?? "").trim();
  return COUNTRY_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** English / Arabic, with another stored language shown as the current value "(no longer offered)". */
export function languageOptionsFor(stored: string | null | undefined): Option[] {
  const value = (stored ?? "").trim();
  if (!value || LANGUAGE_OPTIONS.some((o) => o.value === value)) return [...LANGUAGE_OPTIONS];
  return [{ value, label: `${FORMER_LANGUAGE_NAMES[value.toLowerCase()] ?? value} ${NO_LONGER_OFFERED}` }, ...LANGUAGE_OPTIONS];
}

/** A language's label as the Language section shows it. */
export function languageLabel(stored: string | null | undefined): string {
  const value = (stored ?? "").trim();
  return languageOptionsFor(value).find((o) => o.value === value)?.label ?? value;
}

/** A company's portal address — the one helper the list and the company page both use (B10). */
export const portalAddress = (subdomain: string | null | undefined): string =>
  `${(subdomain ?? "").trim().toLowerCase()}.portal.athlyn.app`;

/** The app's display name: "Athlyn", "Amplify+", else its package. */
export function appDisplayName(appScheme: string | null | undefined): string {
  const pkg = normalizePackage(appScheme);
  if (pkg === ATHLYN_APP_PACKAGE) return "Athlyn";
  if (pkg === AMPLIFY_APP_PACKAGE) return "Amplify+";
  return pkg || "No app set";
}
